import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { ControlsWidgetsDataService } from "../../../services/controls-widgets.data.service";
import { Pages } from "@constants/pages/Pages";
import { DesignImplementationDashboardModel } from "../../../shared/models/DesignImplementationDashboardModel";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { BaseHighchartWidget } from "../BaseHighchartWidget";
import { nameof } from "@methods/jeffs-toolkit";
import { ControlInfoModel } from "@app/controls/models/ControlInfoModel";
import { WidgetFilterKey } from "@app/dashboard/shared/models/WidgetFilterKey";
import { firstValueFrom } from "rxjs";
import { provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/dashboard/i18n/scope";

@Component({
    selector: "controls-designimplementation-widget",
    templateUrl: "../highchart-widget.component.html",
    styleUrls: ["../highchart-widget.component.scss"],
    viewProviders: [provideTranslationScope(scope)]
})
export class DesignImplementationWidgetComponent extends BaseHighchartWidget {
    @ViewChild("chartContainer") chartContainer: ElementRef;

    private controlsWidgetsDataService = inject(ControlsWidgetsDataService);
    private pages = inject(Pages);

    constructor() {
        super();

        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.Organization] =
            nameof<ControlInfoModel>((x) => x.organizationId);
        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.BusinessDimension] =
            nameof<ControlInfoModel>((x) => x.businessDimensionIds);
        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.FrameworkDimension] =
            nameof<ControlInfoModel>((x) => x.frameworkDimensionIds);
    }

    protected async getChartConfig() {
        await this.translation.ready();

        const filters = this.getFilters();
        const data = await firstValueFrom(this.controlsWidgetsDataService
            .getDesignImplementationWidgetInfo(filters));

        this.config.noData =
            !data || data.every((x) => x.designcount === 0 && x.implementationcount === 0);
        if (this.config.noData) {
            return null;
        }

        const mappedData = [
            {
                name: "Design score",
                type: "column",
                data: this.getDataItems(data, true),
            },
            {
                name: "Implementation score",
                type: "column",
                data: this.getDataItems(data, false),
            },
        ];

        const config = this.getBaseChartConfig(null, mappedData);
        config.legend = { enabled: true };
        config.xAxis = {
            type: "category",
            categories: this.getCategories(data),
        };

        config.yAxis = {
            allowDecimals: false,
            title: {
                text: this.translation.t("designImplementationWidget.yAxisTitle"),
            },
        };

        config.tooltip = {
            headerFormat: "<b>{point.key}:</b> {point.y}",
            pointFormat: "",
        };

        config.plotOptions.column = { grouping: true };

        return config;
    }

    getDataItems(data: DesignImplementationDashboardModel[], isDesignCount: boolean) {
        return data.map((element) =>
            isDesignCount ? element.designcount : element.implementationcount
        );
    }

    getCategories(data: DesignImplementationDashboardModel[]) {
        return data.map((element) => element.name);
    }

    protected async openWorkspace(ev) {
        const category = ev.point.category;
        let type;

        switch (ev.point.series.name) {
            case "Design score":
                type = nameof<ControlInfoModel>((c) => c.designScore);
                break;
            case "Implementation score":
                type = nameof<ControlInfoModel>((c) => c.implementationScore);
                break;
        }

        const searchKeywords: SearchKeyword[] = [this.getSearchKeyword(type, category)];

        const preset = await this.getBasePreset(searchKeywords);
        this.openPreset(this.pages.ControlOverview, preset);
    }

    protected async onGlobalOpenClick() {
        const preset = await this.getBasePreset();
        this.openPreset(this.pages.ControlOverview, preset);
    }
}
